import { TFunction } from 'i18next';
import { Actions, State } from '../hooks/useChannelSetup';
import React from 'react';
import RenderInputField from './RenderInputField';
import { Switcher, Typography } from '~/UIKit';

const RenderIntegrationFields = ({
  integrationType,
  state,
  actions,
  t,
  classes,
  mode,
  disabled,
}: {
  integrationType: string;
  state: State;
  actions: Actions;
  t: TFunction;
  classes: Record<string, string>;
  mode: string;
  disabled?: boolean;
}) => {
  switch (integrationType) {
    case 'magicline':
      return (
        <>
          <RenderInputField
            label={t('magicline.setup.base.url')}
            value={state.baseUrl}
            setter={actions.setBaseUrl}
            placeholder={t('magicline.setup.base.url.placeholder')}
            classes={classes}
          />
          <RenderInputField
            label={t('magicline.setup.api.token')}
            value={state.apiKey}
            setter={actions.setApiKey}
            placeholder={t('magicline.setup.api.token.placeholder')}
            classes={classes}
            isSensitive
          />
          <RenderInputField
            label={t('magicline.setup.webhook.token')}
            value={state.webHookKey}
            setter={actions.setWebHookKey}
            placeholder={t('magicline.setup.webhook.token.placeholder')}
            classes={classes}
            isSensitive
          />
        </>
      );
    case 'eversports':
      return (
        <>
          <RenderInputField
            label={t('eversports.setup.endpoint')}
            value={state.endpoint || ''}
            setter={actions.setEndpoint}
            placeholder={t('eversports.setup.endpoint.placeholder')}
            classes={classes}
            disabled={mode === 'edit'}
          />
          <RenderInputField
            label={t('eversports.setup.accessToken')}
            value={state.accessToken || ''}
            setter={actions.setAccessToken}
            placeholder={t('eversports.setup.accessToken.placeholder')}
            classes={classes}
            disabled={mode === 'edit'}
            isSensitive
          />
          <label htmlFor="livestream" className={classes.inputMagicline}>
            <Typography variant="body2" className={classes.selectLabel}>
              {t('eversports.setup.livestream')}
            </Typography>
            <Switcher
              data-testid="magicline-switch"
              checked={state.livestream}
              onChange={() => actions.setLivestream(!state.livestream)}
              disabled={disabled}
              classes={{
                root: classes.switchRoot,
                switchBase: classes.switchBase,
                thumb: classes.thumb,
                track: classes.track,
                checked: classes.checked,
              }}
            />
          </label>
          {!state.livestream && (
            <RenderInputField
              label={t('eversports.setup.location.uuid')}
              value={state.locationId}
              setter={actions.setLocationId}
              disabled={disabled}
              placeholder={t('eversports.setup.location.uuid.placeholder')}
              classes={classes}
            />
          )}
          {state.livestream && (
            <RenderInputField
              label={t('eversports.setup.venue.uuid')}
              value={state.venueUUID}
              setter={actions.setVenueUUID}
              disabled={disabled}
              placeholder={t('eversports.setup.venue.uuid.placeholder')}
              classes={classes}
            />
          )}
        </>
      );
    case 'virtuagym':
      return (
        <RenderInputField
          label={t('virtuagym.setup.club.id')}
          value={state.clubId}
          setter={actions.setClubId}
          placeholder={t('virtuagym.setup.club.id.placeholder')}
          classes={classes}
        />
      );
    case 'zingfit':
      return (
        <>
          <RenderInputField
            label={t('zingfit.setup.url')}
            value={state.url}
            setter={actions.setUrl}
            placeholder={t('zingfit.setup.url.placeholder')}
            classes={classes}
            disabled
          />
          <RenderInputField
            label={t('zingfit.setup.clientId')}
            value={state.clientId}
            setter={actions.setClientId}
            placeholder={t('zingfit.setup.clientId.placeholder')}
            classes={classes}
          />
          <RenderInputField
            label={t('zingfit.setup.clientSecret')}
            value={state.clientSecret}
            setter={actions.setClientSecret}
            placeholder={t('zingfit.setup.clientSecret.placeholder')}
            classes={classes}
            isSensitive
          />
          <RenderInputField
            label={t('zingfit.setup.regionId')}
            value={state.regionId}
            setter={actions.setRegionId}
            placeholder={t('zingfit.setup.regionId.placeholder')}
            classes={classes}
          />
          <RenderInputField
            label={t('zingfit.setup.siteId')}
            value={state.siteId}
            setter={actions.setSiteId}
            placeholder={t('zingfit.setup.siteId.placeholder')}
            classes={classes}
          />
          <RenderInputField
            label={t('zingfit.setup.activationCode')}
            value={state.activationCode}
            setter={actions.setActivationCode}
            placeholder={t('zingfit.setup.activationCode.placeholder')}
            classes={classes}
          />
        </>
      );

    case 'virtuagymv1':
      return (
        <>
          <RenderInputField
            label={t('virtuagymV1.setup.club.id')}
            value={state.clubId}
            setter={actions.setClubId}
            placeholder={t('virtuagymV1.setup.club.id.placeholder')}
            classes={classes}
          />
          <RenderInputField
            label={t('virtuagymV1.setup.club.key')}
            value={state.clubKey}
            setter={actions.setClubKey}
            placeholder={t('virtuagymV1.setup.club.key.placeholder')}
            classes={classes}
            isSensitive
          />
          <RenderInputField
            label={t('virtuagymV1.setup.schedule.id')}
            value={state.scheduleId}
            setter={(value: string) => actions.setScheduleId(value)}
            placeholder={t('virtuagymV1.setup.schedule.id.placeholder')}
            classes={classes}
            tooltipMessage={t('integrations.virtualgymV1.tooltip.scheduleId')}
          />
        </>
      );
    case 'mindbody':
      return (
        <>
          <RenderInputField
            label={t('mindbody.setup.site.id')}
            value={state.siteId}
            setter={actions.setSiteId}
            placeholder={t('mindbody.setup.site.id.placeholder')}
            classes={classes}
          />
          <RenderInputField
            label={t('mindbody.setup.location.id')}
            value={state.locationId}
            setter={actions.setLocationId}
            placeholder={t('mindbody.setup.location.id.placeholder')}
            classes={classes}
          />
          <RenderInputField
            label={t('mindbody.setup.service.id')}
            value={state.serviceId}
            setter={actions.setServiceId}
            placeholder={t('mindbody.setup.service.id.placeholder')}
            classes={classes}
          />
        </>
      );
    case 'resamania':
      return (
        <>
          <RenderInputField
            label={t('resamania.setup.countryCode')}
            value={state.countryCode}
            setter={actions.setCountryCode}
            placeholder={t('resamania.setup.countryCode.placeholder')}
            classes={classes}
          />
          <RenderInputField
            label={t('resamania.setup.studioId')}
            value={state.studioId}
            setter={actions.setStudioId}
            placeholder={t('resamania.setup.studioId.placeholder')}
            classes={classes}
          />
          <RenderInputField
            label={t('resamania.setup.clubID')}
            value={state.clubID}
            setter={actions.setClubID}
            placeholder={t('resamania.setup.clubID.placeholder')}
            classes={classes}
          />
          <RenderInputField
            label={t('resamania.setup.networkId')}
            value={state.networkId}
            setter={actions.setNetworkId}
            placeholder={t('resamania.setup.networkId.placeholder')}
            classes={classes}
          />
          <RenderInputField
            label={t('resamania.setup.clientToken')}
            value={state.clientToken}
            setter={actions.setClientToken}
            placeholder={t('resamania.setup.clientToken.placeholder')}
            classes={classes}
            isSensitive
          />
        </>
      );
    default:
      return null;
  }
};

export default RenderIntegrationFields;
